import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Button,Col, Row } from "reactstrap";
import {
  Radio,
  Space,
  Table,
  Tag,
  Modal,
  message,
  Card,
  Divider,
  Empty,
  Button as AntdButton,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import { WorkRequestFormData, WorkRequestLine, WorkLineSource } from "../types";
import ProjectTaskSelector from "./ProjectTaskSelector";
import WorkBoqLineSelector from "./WorkBoqLineSelector";
import NewWorkLineCreator from "./NewWorkLineCreator";
import Swal from "sweetalert2";

const WorkRequestDetails: React.FC = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WorkRequestFormData>();

  const [selectedSource, setSelectedSource] = useState<WorkLineSource | null>(
    null
  );
  const [workRequestLines, setWorkRequestLines] = useState<WorkRequestLine[]>(
    []
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLine, setEditingLine] = useState<WorkRequestLine | null>(null);
  const [isSourceLocked, setIsSourceLocked] = useState(false);

  // Watch form values
  const AllFormDetails = watch();
  const projectIdWatch = watch("projectId");

  // Initialize work request lines and source selection from form
  useEffect(() => {
    if (
      AllFormDetails.workRequestLines &&
      AllFormDetails.workRequestLines.length > 0 &&
      workRequestLines.length === 0
    ) {
      setWorkRequestLines(AllFormDetails.workRequestLines);
    }

    // Restore source selection and lock state from form
    if (AllFormDetails.selectedSource && !selectedSource) {
      setSelectedSource(AllFormDetails.selectedSource);
    }

    if (AllFormDetails.isSourceLocked !== undefined && !isSourceLocked) {
      setIsSourceLocked(AllFormDetails.isSourceLocked);
    }
  }, [
    AllFormDetails.workRequestLines,
    AllFormDetails.selectedSource,
    AllFormDetails.isSourceLocked,
    workRequestLines.length,
    selectedSource,
    isSourceLocked,
  ]);

  const handleSourceChange = async (e: any) => {
    const newSource = e.target.value;

    // If source is locked and lines exist, show confirmation popup
    if (
      isSourceLocked &&
      workRequestLines.length > 0 &&
      newSource !== selectedSource
    ) {
      const result = await Swal.fire({
        title: "Change Source?",
        text: "Changing the source will delete all added work lines. Do you want to continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, change source",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        setWorkRequestLines([]);
        setValue("workRequestLines", []);
        setSelectedSource(newSource);
        setValue("selectedSource", newSource);
        setIsSourceLocked(false);
        setValue("isSourceLocked", false);
        message.success("Source changed and work lines cleared");
      }
    } else {
      setSelectedSource(newSource);
      setValue("selectedSource", newSource);
    }
  };

  const addWorkRequestLine = () => {
    if (!selectedSource) {
      message.error("Please select a source before adding work lines");
      return;
    }
    setEditingLine(null);
    setShowAddModal(true);
  };

  const editWorkRequestLine = (line: WorkRequestLine) => {
    setEditingLine(line);
    setSelectedSource(line.source as WorkLineSource);
    setShowAddModal(true);
  };

  const deleteWorkRequestLine = (lineId: string) => {
    Swal.fire({
      title: "Delete Work Line",
      text: "Are you sure you want to delete this work line?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedLines = workRequestLines.filter(
          (line) => line.id !== lineId
        );
        setWorkRequestLines(updatedLines);
        setValue("workRequestLines", updatedLines);
        message.success("Work line deleted successfully");
      }
    });
  };

  // Duplicate validation based on source
  const checkForDuplicates = (
    newLines: WorkRequestLine[],
    existingLines: WorkRequestLine[],
    editingLineId?: string
  ) => {
    const duplicates: string[] = [];

    newLines.forEach((newLine) => {
      const filteredExisting = editingLineId
        ? existingLines.filter((line) => line.id !== editingLineId)
        : existingLines;

      const isDuplicate = filteredExisting.some((existingLine) => {
        switch (newLine.source) {
          case WorkLineSource.ProjectManagement:
            // No duplicate tasks (taskTitle + sourceId)
            return (
              existingLine.taskTitle === newLine.taskTitle &&
              existingLine.sourceId === newLine.sourceId &&
              existingLine.source === newLine.source
            );

          case WorkLineSource.WorkBOQ:
            // No duplicate LineIds
            return (
              existingLine.sourceId === newLine.sourceId &&
              existingLine.source === newLine.source
            );

          case WorkLineSource.NewWorkLine:
            // No duplicate Task Titles
            return (
              existingLine.taskTitle === newLine.taskTitle &&
              existingLine.source === newLine.source
            );

          default:
            return false;
        }
      });

      if (isDuplicate) {
        duplicates.push(newLine.taskTitle);
      }
    });

    return duplicates;
  };

  const handleLinesSelected = (lines: WorkRequestLine[]) => {
    let updatedLines: WorkRequestLine[];

    if (editingLine) {
      // Replace existing line - check for duplicates excluding the current line
      const duplicates = checkForDuplicates(
        lines,
        workRequestLines,
        editingLine.id
      );

      if (duplicates.length > 0) {
        message.error(`Duplicate line detected: ${duplicates.join(", ")}`);
        return;
      }

      updatedLines = workRequestLines.map((line) =>
        line.id === editingLine.id ? lines[0] : line
      );
      message.success("Work line updated successfully");
    } else {
      // Add new lines - check for duplicates
      const duplicates = checkForDuplicates(lines, workRequestLines);

      if (duplicates.length > 0) {
        message.error(
          `Duplicate lines detected: ${duplicates.join(
            ", "
          )}. Please select different items.`
        );
        return;
      }

      const newLines = lines.map((line) => ({
        ...line,
        id: `${Date.now()}-${Math.random()}`,
      }));
      updatedLines = [...workRequestLines, ...newLines];
      message.success(`${lines.length} work line(s) added successfully`);

      // Lock the source once lines are added
      if (!isSourceLocked) {
        setIsSourceLocked(true);
        setValue("isSourceLocked", true);
      }
    }

    // Update both local state and form state
    setWorkRequestLines(updatedLines);
    setValue("workRequestLines", updatedLines);
    setShowAddModal(false);
  };

  // Material BOQ expandable row component
  const expandedRowRender = (record: WorkRequestLine) => {
    const materialAllocation = (record as any).materialAllocation;

    if (!materialAllocation || materialAllocation.length === 0) {
      return null;
    }

    const materialColumns = [
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        width: "40%",
        render: (name: string, material: any) => (
          <div>
            <div className="fw-bold">{name}</div>
            {material.category && (
              <Tag color="blue" style={{ marginTop: 4 }}>
                {material.category}
              </Tag>
            )}
          </div>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: "20%",
        align: "right" as const,
        render: (qty: number) => <strong>{qty.toLocaleString()}</strong>,
      },
      {
        title: "UOM",
        dataIndex: "uom",
        key: "uom",
        width: "20%",
        align: "center" as const,
        render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
      },
      {
        title: "Material ID",
        dataIndex: "materialId",
        key: "materialId",
        width: "20%",
        render: (id: string) => <code className="text-muted">{id}</code>,
      },
    ];

    return (
      <div
        style={{
          margin: "16px 0",
          backgroundColor: "#fafafa",
          padding: "16px",
          borderRadius: "6px",
        }}
      >
        <div className="mb-2">
          <strong>
            📦 Material BOQ Allocation ({materialAllocation.length} items)
          </strong>
        </div>
        <Table
          columns={materialColumns}
          dataSource={materialAllocation}
          pagination={false}
          size="small"
          rowKey="id"
          bordered
        />
      </div>
    );
  };

  // Table columns for work request lines display (FSD Format)
  const columns = [
    {
      title: "S.No.",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div>{text}</div>
          {record.subcategory && (
            <small className="text-muted">{record.subcategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 130,
      align: "right" as const,
      render: (quantity: number, record: WorkRequestLine) => {
        // Show available vs requested quantity
        const availableQty = (record as any).availableQuantity || quantity;
        const requestedQty = (record as any).requestedQuantity || quantity;

        return (
          <div>
            <div className="text-muted small">
              Available: {availableQty.toLocaleString()}
            </div>
            <div>
              <strong>Requested: {requestedQty.toLocaleString()}</strong>{" "}
              {record.uom}
            </div>
          </div>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source: WorkLineSource, record: WorkRequestLine) => {
        const colors = {
          [WorkLineSource.ProjectManagement]: "blue",
          [WorkLineSource.WorkBOQ]: "green",
          [WorkLineSource.NewWorkLine]: "orange",
        };
        const materialAllocation = (record as any).materialAllocation;
        const hasMaterialBOQ =
          materialAllocation && materialAllocation.length > 0;

        return (
          <div>
            <Tag color={colors[source]}>{source}</Tag>
            {hasMaterialBOQ && (
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: "10px" }}>
                  📦 Material BOQ
                </Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Tower/Villa",
      key: "location",
      width: 120,
      render: (_: any, record: WorkRequestLine) => {
        if (record.assignedTower) return record.assignedTower;
        if (record.assignedVilla) return record.assignedVilla;
        return "-";
      },
    },
    {
      title: "Planned Dates",
      key: "plannedDates",
      width: 150,
      render: (_: any, record: WorkRequestLine) => (
        <div>
          {record.plannedStartDate && (
            <div>
              <small>Start: {record.plannedStartDate}</small>
            </div>
          )}
          {record.plannedEndDate && (
            <div>
              <small>End: {record.plannedEndDate}</small>
            </div>
          )}
          {!record.plannedStartDate && !record.plannedEndDate && "-"}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: WorkRequestLine) => (
        <Space>
          <AntdButton
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => editWorkRequestLine(record)}
            style={{ padding: "4px 8px" }}
          >
            Edit
          </AntdButton>
          <AntdButton
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteWorkRequestLine(record.id)}
            style={{ padding: "4px 8px" }}
          >
            Delete
          </AntdButton>
        </Space>
      ),
    },
  ];

  const validateForm = (data: WorkRequestFormData) => {
    if (workRequestLines.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please add at least one work request line",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (data: WorkRequestFormData) => {
    if (!validateForm(data)) return;

    const payload = {
      ...data,
      workRequestLines,
      selectedSource,
      isSourceLocked,
      step: "workRequestDetails",
    };

    console.log("Work Request Details Payload:", payload);
    props.next(payload);
  };

  const renderSourceSelector = () => {
    switch (selectedSource) {
      case WorkLineSource.ProjectManagement:
        return (
          <ProjectTaskSelector
            projectId={projectIdWatch}
            onLinesSelected={handleLinesSelected}
            selectedLines={editingLine ? [editingLine] : []}
            editingLine={editingLine}
          />
        );
      case WorkLineSource.WorkBOQ:
        return (
          <WorkBoqLineSelector
            onLinesSelected={handleLinesSelected}
            selectedLines={editingLine ? [editingLine] : []}
            editingLine={editingLine}
          />
        );
      case WorkLineSource.NewWorkLine:
        return (
          <NewWorkLineCreator
            onLineCreated={(line) => handleLinesSelected([line])}
            editingLine={editingLine}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <div className="card">
          <div className="card-body">
            {/* <div className="card-title">
              <h5>🧩 2. Work Request Details Section</h5>
              <p className="text-muted">Add work lines from different sources as specified in FSD</p>
            </div> */}

            {/* Source Selection */}
            <Card className="mb-4" title="📋 Select Work Line Source">
              <div className="mb-3">
                {/* {isSourceLocked && workRequestLines.length > 0 && (
                  <div className="alert alert-warning mb-3">
                    <Icon name="lock" className="me-2" />
                    <strong>Source Locked:</strong> Source is locked after adding work lines. Changing it will delete all added work lines.
                  </div>
                )} */}
                <Radio.Group
                  value={selectedSource}
                  onChange={handleSourceChange}
                  className="w-100"
                >
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Radio
                        value={WorkLineSource.ProjectManagement}
                        className="w-100"
                      >
                        <div
                          className="p-3 border rounded"
                          style={{ marginLeft: "24px" }}
                        >
                          <strong>From Project Management</strong>
                          <div className="text-muted small mt-1">
                            Based on selected project, fetch activities and
                            tasks
                          </div>
                        </div>
                      </Radio>
                    </Col>
                    <Col span={8}>
                      <Radio value={WorkLineSource.WorkBOQ} className="w-100">
                        <div
                          className="p-3 border rounded"
                          style={{ marginLeft: "24px" }}
                        >
                          <strong>From Work BOQ</strong>
                          <div className="text-muted small mt-1">
                            Select work lines from existing BOQs with categories
                          </div>
                        </div>
                      </Radio>
                    </Col>
                    <Col span={8}>
                      <Radio
                        value={WorkLineSource.NewWorkLine}
                        className="w-100"
                      >
                        <div
                          className="p-3 border rounded"
                          style={{ marginLeft: "24px" }}
                        >
                          <strong>New Work Line</strong>
                          <div className="text-muted small mt-1">
                            Create new work line with custom details
                          </div>
                        </div>
                      </Radio>
                    </Col>
                  </Row>
                </Radio.Group>
              </div>

              {!selectedSource && (
                <div className="alert alert-info">
                  <Icon name="info" className="me-2" />
                  Please select a source above before adding work lines.
                </div>
              )}
            </Card>

            {/* Work Request Lines Table */}
            <Card
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <span>Work Request Lines</span>
                  <Button
                    // type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={addWorkRequestLine}
                    disabled={!selectedSource}
                    title={
                      !selectedSource
                        ? "Please select a source first"
                        : "Add work line"
                    }
                  >
                    Add Work Line
                  </Button>
                </div>
              }
              className="mb-4"
            >
              {workRequestLines.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={workRequestLines}
                  pagination={false}
                  scroll={{ x: 1000 }}
                  size="small"
                  rowKey="id"
                  bordered
                  className="customTable"
                  expandable={{
                    expandedRowRender,
                    expandIcon: ({ expanded, onExpand, record }) => {
                      const materialAllocation = (record as any)
                        .materialAllocation;
                      const hasMaterialBOQ =
                        materialAllocation && materialAllocation.length > 0;

                      if (!hasMaterialBOQ) return null;

                      return expanded ? (
                        <DownOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#1890ff", cursor: "pointer" }}
                        />
                      ) : (
                        <RightOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#1890ff", cursor: "pointer" }}
                        />
                      );
                    },
                    rowExpandable: (record) => {
                      const materialAllocation = (record as any)
                        .materialAllocation;
                      return (
                        materialAllocation && materialAllocation.length > 0
                      );
                    },
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <p>No work lines added yet</p>
                      {/* <Button
                        // type="primary"
                        icon={<PlusOutlined />}
                        onClick={addWorkRequestLine}
                        disabled={!selectedSource}
                        title={!selectedSource ? "Please select a source first" : "Add first work line"}
                      >
                        Add First Work Line
                      </Button> */}
                    </div>
                  }
                />
              )}
            </Card>

            {/* Summary Information */}
            {/* {workRequestLines.length > 0 && (
              <div className="alert alert-info">
                <Icon name="info" className="me-2" />
                <strong>Summary:</strong> {workRequestLines.length} work line(s) added from{' '}
              </div>
            )} */}
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
                disabled={workRequestLines.length === 0}
              >
                Next
              </Button>
            </li>
            <li>
              <Button color="light" 
              type="button"
               onClick={props.prev}>
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Source Selection Modal */}
      <Modal
        title={`${editingLine ? "Edit" : "Add"} Work Line`}
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div className="mb-4">
          {/* <h6>📝 {editingLine ? 'Edit' : 'Add'} Work Line Details:</h6> */}
          <p className="text-muted small mb-3">
            Source:{" "}
            <Tag
              color={
                selectedSource === WorkLineSource.ProjectManagement
                  ? "blue"
                  : selectedSource === WorkLineSource.WorkBOQ
                  ? "green"
                  : "orange"
              }
            >
              {selectedSource}
            </Tag>
            {/* {isSourceLocked && <Tag color="red" className="ms-2">🔒 Locked</Tag>} */}
          </p>
        </div>

        {/* <Divider /> */}

        {selectedSource && renderSourceSelector()}
      </Modal>
    </>
  );
};

export default WorkRequestDetails;
